
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import DataFieldCache from "../../../models/data.field.cache";
import DataFieldBar from "@/components/study/datafields/base/DataFieldBar.vue";
import dataFieldTypes from "@/dataFieldTypes";
import YesNoDialog from "@/components/common/YesNoDialog.vue";
import {
  currentUserModule,
  dataFieldsEditModule,
  instrumentModule, safetyModule, studyPatientContext
} from "@/store";
import {stringify} from "@/auxillary/datafield.stringify";
import EventReportSignatureDialog from "@/components/eventreport/EventReportSignatureDialog.vue";
import EventReportDeactivationDialog from "@/components/eventreport/EventReportDeactivationDialog.vue";
import {eventBus} from "@/main";
import {convertToCreateDtos, convertToUpdateDtos, findParentField} from "@/auxillary/datafield.cache.helpers";
import {MdbsCore} from "@/api/tsclient";
import DataFieldDto = MdbsCore.DataFieldDto;
import ValidationMessageDto = MdbsCore.ValidationMessageDto;
import {deepCopy} from "@/auxillary";
import {fieldsWithValuesWillBeDeleted, IDataFieldDeletionReport} from "@/auxillary/datafield.deletion";
import DeletedDataFieldsDialog from "@/components/study/dataFieldDeletion/DataFieldsDeletedDialog.vue";
import {
  canDeactivateEventReports,
  canDeleteEvents,
  canSignEventReports,
  eventReportIsInitial
} from "@/auxillary/eventReport";
import {DateTime} from "luxon";
import SignatureDataDto = MdbsCore.SignatureDataDto;
import {formatDateTime} from "@/auxillary/date-format";

const AfterSaveSignEventReportAction = "sign-event-report";

@Component({
  name: 'EventReportListEntryField',
  components: {
    EventReportDeactivationDialog,
    EventReportSignatureDialog,
    ...dataFieldTypes,
    DataFieldBar,
    YesNoDialog,
    DeletedDataFieldsDialog
  },
})
export default class EventReportListEntryField extends Vue {

  @Prop({required: true})
  private dataField: DataFieldCache

  @Prop({required: true})
  private isEditing: boolean

  errorMessage: string = "";
  doShowDeleteDialog = false;
  doShowDeactivationDialog = false;
  doShowDeactivationSignatureDialog = false;
  doShowSignatureDialog = false;
  signature: SignatureDataDto | null = null;
  saving = false;

  showDeletionDialog = false;
  deletedFields: Array<IDataFieldDeletionReport> = [];
  saveContinue = false;

  @Watch('isEditing', {immediate: true})
  async onIsEditingChange(isEditing: boolean | null) {
    if (isEditing == null) {
      return;
    }

    if (isEditing) {
      // register events
      eventBus.$on("save-open-report", async () => {
        await this.saveEventReport(true);
      });
    } else {
      // unregister old events - necessary if instrument view is opened multiple times
      eventBus.$off("save-open-report");
    }
  }

  get field() {
    return this.dataField;
  }

  get isNewField() {
    return this.field?.isNewField ?? false;
  }

  /*
  The field has an event report that was sent (processed by aeReportSender).
   */
  private get hasSentEventReport(): boolean {
    return this.field.values.eventReportSendDate != undefined;
  }

  get eventReportSendDate(): string {
    if (this.field.values.eventReportSendDate == undefined)
      return "";
    return DateTime.fromFormat(this.field.values.eventReportSendDate, "yyyy-MM-dd_HH'h'mm'm'ss's'").toFormat("dd.MM.yyyy");
  }


  /*
  The field has an event report no matter if it was sent.
   */
  private get hasEventReport(): boolean {
    return this.field.values.eventReportIdentifier != undefined;
  }

  /*
  The field has an event report no matter if it was sent.
   */
  private get eventReportIdentifier(): string {
    return this.field.values.eventReportIdentifier;
  }

  get eventReportInfo(): string {
    if (!this.hasEventReport)
      return "";
    if (!this.hasSentEventReport)
      return "(" + this.eventReportIdentifier + ")";
    return "(" + this.eventReportIdentifier + " Sendedatum: " + this.eventReportSendDate + ")";
  }

  /*
  The field can be deleted if there is no sent event report.
   */
  get reportCanBeDeleted() {
    return this.field.isNewField || (!this.hasSentEventReport && eventReportIsInitial(this.field));
  }

  /*
  The field (technically: its event report) can be signed if there is an unsigned event report.
   */
  get reportCanBeSigned() {
    return canSignEventReports() && this.hasEventReport && !this.hasSignature;
  }

  get hasSignature() {
    return this.field?.tags?.signature != null && this.field?.tags?.signatureId != null;
  }

  get hasDeactivationSignature() {
    return this.field?.tags?.deactivationReason != null && this.field?.tags?.signatureId != null;
  }

  get signatureId() {
    return this.field?.tags?.signatureId;
  }

  get signatureText() {
    return this.signature ?
        "Meldung signiert: <br/>"
        + this.signature.signingUserDisplayName
        + " (" + this.signature.signingUserName + ") "
        + formatDateTime(this.signature.timeStamp) + "<br/>"
        + this.signature.reason : "";
  }

  get isDeactivated() {
    return this.field?.tags?.isDeactivated === true
  }

  get deactivationSignatureText() {
    return this.signature ?
        "Deaktivierungsgrund: "
        + this.signature.signingUserDisplayName
        + " (" + this.signature.signingUserName + ") "
        + formatDateTime(this.signature.timeStamp) + "<br/>"
        + this.signature.reason : "";
  }

  get deactivationReason() {
    return this.field?.tags?.deactivationReason ?? "";
  }

  get isVisible() {
    return this.dataField?.isVisible ?? false;
  }

  get childNames() {
    return this.dataField.definition.names ?? [{
      template: "{0}",
      field: ["name"]
    }];
  }

  get childName() {

    let name = "";

    for (const childName of this.childNames) {
      // find field
      let field: any = undefined;
      for (const fieldIdentifier of childName.field) {
        if (field === undefined) {
          field = this.childFields.find(d => d.identifier == fieldIdentifier)
          if (field === undefined)
            break;
          continue;
        }

        field = field.childFields.find((d: any) => d.identifier == fieldIdentifier)
        if (field === undefined)
          break;
      }
      if (field?.values == null)
        continue;

      const fieldName = stringify(field);
      if (fieldName)
        name += childName.template.replace("{0}", fieldName);
    }

    if (name == "")
      return "(keine Eintragung)";

    return name;
  }

  get canDeleteOrDeactivateEvent() {
    return this.isEditable && ((canDeactivateEventReports() && !this.reportCanBeDeleted) || (canDeleteEvents() && this.reportCanBeDeleted));
  }

  get isEditable() {
    return this.dataField?.isEditable ?? false;
  }

  get childHasMessage() {
    const allChildFields = this.childFields.traverse(d => d.childFields) ?? []
    for (let childField of allChildFields) {
      if (dataFieldsEditModule.validationMessages
          .some(vm => vm.dataFieldId == childField.id))
        return true;
    }
    return false;
  }

  get displayName() {
    return this.dataField?.displayName;
  }

  get stringifyValue() {
    return stringify(this.dataField)
  }

  get childFields() {
    return this.dataField?.childFields ?? [];
  }

  created() {
    this.doShowSignatureDialog = false;
  }

  async mounted() {
    // should open sign dialog after save?
    const openSignDialog = dataFieldsEditModule.afterSaveAction == AfterSaveSignEventReportAction
        && this.dataField?.id !== undefined
        && dataFieldsEditModule.lastEditedFieldId == this.dataField?.id
        && this.reportCanBeSigned
        && currentUserModule.user?.role?.identifier?.toLowerCase() == "physician";
    // is it me you looking for?
    if (openSignDialog) {
      Vue.nextTick(() => {
        this.showSignatureDialog()
        dataFieldsEditModule.resetAfterSaveAction();
      });
    }

    if (this.hasSignature || this.hasDeactivationSignature)
      this.signature = await safetyModule.getSignatureById({id: this.signatureId ?? ""})
  }

  showSignatureDialog() {
    if (this.isEditable && !this.hasSignature)
      this.doShowSignatureDialog = true;
  }

  showDeleteOrDeactivate() {
    // unsaved fields can be deleted
    if (this.reportCanBeDeleted) {
      this.doShowDeleteDialog = true;
      return
    }

    this.doShowDeactivationDialog = true;
  }

  deactivateEntry() {
    this.doShowDeactivationDialog = false
    this.doShowDeactivationSignatureDialog = true;
  }

  async deleteEntry() {
    if (this.field.isNewField) {
      dataFieldsEditModule.removeField(this.dataField);
    } else {
      await instrumentModule.deleteDataField({id : this.dataField.id, reasonOfChange: undefined});
      await eventBus.$emit("reload-instrument");
    }

    this.doShowDeleteDialog = false
  }

  private async handleSaveSignature() {
    this.doShowSignatureDialog = false;
    this.$emit("onSaveEntry", "");
    await eventBus.$emit("reload-instrument");
  }

  private async handleDeactivation() {
    this.doShowSignatureDialog = false;
    this.$emit("onSaveEntry", "");
    await eventBus.$emit("reload-instrument");
  }

  async checkForFieldDeletionBeforeSave() {
    const deletedFields = fieldsWithValuesWillBeDeleted(dataFieldsEditModule.dataFields);
    if (deletedFields.length > 0) {
      this.showDeletionDialog = true;
      this.deletedFields = deletedFields;
    } else {
      await this.saveEventReport();
    }
  }

  private async saveEventReport(fromContinue: boolean = false) {
    this.saving = true;
    this.$emit("onSaveEntry", this.field.id);


    const instrument = studyPatientContext.standaloneInstruments.find(i => i.id == instrumentModule.instrument.id);
    // instrument id is set after first save try, even with invalid data
    if (!fromContinue && (!instrumentModule.instrument.id || !instrument)) {
      await eventBus.$emit("save-instrument");
      this.saving = false;
      dataFieldsEditModule.setAfterSaveAction(AfterSaveSignEventReportAction);
      return;
    }

    // Save this field only
    const parentField = findParentField(this.field, dataFieldsEditModule.dataFields);

    if (this.field.isNewField) {
      const createDtos = convertToCreateDtos([this.field]);
      if (createDtos.length != 1) return;

      await instrumentModule.createDataField({
        field: createDtos[0],
        definitionId: this.field.definitionId,
        parentFieldId: parentField?.id,
        displayKey: this.field.displayKey,
        identifier: this.field.identifier,
        order: this.field.order,
      });

    } else {
      const updateDtos = convertToUpdateDtos([this.field]);
      if (updateDtos.length != 1) return;

      await instrumentModule.updateDataField({
        id: this.field.id,
        field: updateDtos[0],
        parentFieldId: parentField?.id,
        displayKey: this.field.displayKey,
        identifier: this.field.identifier,
        order: this.field.order,
      });

    }


    // Insert ask for signature dialog

    // only reload instrument if it was saved, needs messages otherwise
    if (instrumentModule.instrument.canSave) {
      dataFieldsEditModule.setAfterSaveAction(AfterSaveSignEventReportAction);
      await eventBus.$emit("reload-instrument");
    } else {

      const messages = deepCopy(instrumentModule.instrument?.validationMessages ?? []);

      if (this.field.isNewField)
        this.mapValidationMessages(messages, dataFieldsEditModule.dataFields, instrumentModule.instrument.fields ?? [])
      dataFieldsEditModule.setValidationMessages(messages)
    }

    this.saving = false;
  }

  public mapValidationMessages(messages: ValidationMessageDto[], oldFields: Array<DataFieldCache>, newFields: Array<DataFieldDto>) {
    for (let i = 0; i < oldFields.length; i++) {
      const oldField = oldFields[i];
      const newField = newFields[i];
      for (let message of messages.filter(m => m.dataFieldId == newField?.id)) {
        message.dataFieldId = oldField.id;
      }
      this.mapValidationMessages(messages, oldField.childFields, newField?.childFields ?? [])
    }
  }


  public toggleEditing() {
    if (this.isEditing) {
      this.stopEditing();
      return;
    }

    this.startEditing();
  }

  public startEditing() {
    this.$emit("onStartEditing", this.field.id);
  }

  public stopEditing() {
    this.$emit("onCancelEntry")

    if (this.isEditable) {
      eventBus.$emit("reload-instrument"); // ??
    }
  }
}
